import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useProfileCompletion } from "../hooks/useProfileCompletion";
import reimbursementV2ApiService, {
  ReimbursementV2Request,
  ReimbursementV2Item,
} from "../services/reimbursement-v2-api";
import reimbursementCategoryApiService, {
  ReimbursementCategory,
} from "../services/reimbursement-category-api";
import taxClassificationApiService, {
  TaxClassification,
} from "../services/tax-classification-api";
import userManagementApiService from "../services/user-management-api";
import { Employee } from "../types/leave";
import FileUpload from "../components/shared/FileUpload";
import { mergeFilesToPdf, replaceFirstPageOfPdf } from "../utils/pdfUtils";
import { formatMergedPdfFileName } from "../utils/fileUtils";
import { CURRENCIES, formatCurrencyDisplay } from "../utils/currencyData";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import PdfViewer from "../components/shared/PdfViewer";
import SubmitFeedback from "../components/shared/SubmitFeedback";

const ReimbursementV2Page: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const { isComplete: isProfileComplete } = useProfileCompletion();
  const [requests, setRequests] = useState<ReimbursementV2Request[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRequest, setEditingRequest] =
    useState<ReimbursementV2Request | null>(null);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [categories, setCategories] = useState<ReimbursementCategory[]>([]);
  const [taxClassifications, setTaxClassifications] = useState<
    TaxClassification[]
  >([]);
  const [mergedPdfFileName, setMergedPdfFileName] = useState<string>("");
  const [employeeData, setEmployeeData] = useState<Employee | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const [attachmentAvailability, setAttachmentAvailability] = useState<
    Record<string, boolean>
  >({});
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string>("");
  const [selectedPdfFileName, setSelectedPdfFileName] = useState<string>("");
  // Helper function to get local date in YYYY-MM-DD format
  const getLocalDate = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState<ReimbursementV2Request>({
    categoryId: 0,
    taxClassificationId: 1,
    submissionDate: getLocalDate(), // Will be ignored by backend, but kept for compatibility
    itemDetails: "",
    vendor: "",
    totalAmount: 0,
    items: [],
  });

  // Memoize categories menu items to prevent expensive re-renders
  const categoriesMenuItems = useMemo(
    () =>
      categories.map((cat) => (
        <MenuItem key={cat.id} value={cat.id}>
          {cat.englishName}
        </MenuItem>
      )),
    [categories],
  );

  // Memoize tax classifications menu items to prevent expensive re-renders
  const taxClassificationsMenuItems = useMemo(
    () =>
      taxClassifications.map((tax) => (
        <MenuItem key={tax.id} value={tax.id}>
          {tax.displayText} {tax.taxPercent ? `(${tax.taxPercent}%)` : ""}
        </MenuItem>
      )),
    [taxClassifications],
  );

  // Memoized sx objects to prevent re-creation on every render
  const errorBorderSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#d32f2f",
          borderWidth: 2,
        },
      },
    }),
    [],
  );

  const normalBorderSx = useMemo(() => ({}), []);

  const forexRateJpySx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        backgroundColor: "action.hover",
      },
    }),
    [],
  );

  const forexRateNormalSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
      },
    }),
    [],
  );

  // Memoized form field change handlers to prevent re-renders on every keystroke

  const handleItemDetailsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, itemDetails: e.target.value }));
    },
    [],
  );

  const handleVendorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, vendor: e.target.value }));
    },
    [],
  );

  const handleCategoryChange = useCallback((e: any) => {
    setFormData((prev) => ({ ...prev, categoryId: e.target.value as number }));
  }, []);

  const handleTaxClassificationChange = useCallback((e: any) => {
    setFormData((prev) => ({
      ...prev,
      taxClassificationId: e.target.value as number,
    }));
  }, []);

  // Memoize the relevant form data for PDF filename calculation
  const pdfFileNameData = useMemo(
    () => ({
      date: formData.submissionDate,
      category: formData.categoryId || 0,
      vendor: formData.vendor || t("common.noVendor"),
      item: formData.itemDetails || t("common.noItem"),
      price: formData.totalAmount || 0,
      username: user?.username,
    }),
    [
      formData.submissionDate,
      formData.categoryId,
      formData.vendor,
      formData.itemDetails,
      formData.totalAmount,
      user?.username,
    ],
  );

  // Calculate merged PDF filename when attachments or form data changes
  useEffect(() => {
    // Always generate the filename based on form data, regardless of attachments
    const mergedFileName = formatMergedPdfFileName(pdfFileNameData);
    setMergedPdfFileName(mergedFileName);
  }, [pdfFileNameData]);

  // Load requests, categories, tax classifications, and employee data on component mount
  useEffect(() => {
    if (user?.id) {
      loadRequests();
      loadCategories();
      loadTaxClassifications();
      loadEmployeeData();
    }
  }, [user?.id]);

  const loadEmployeeData = async () => {
    try {
      const employee = await userManagementApiService.getMyProfile();
      setEmployeeData(employee);
    } catch (err) {
      console.error(t("error.failedToLoadEmployeeData"), err);
      // Continue without employee data if it fails
    }
  };

  const loadRequests = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const data =
        await reimbursementV2ApiService.getReimbursementRequestsByEmployee(
          user.id,
        );
      setRequests(data);
      setError(undefined);

      // Check attachment availability for all requests
      const availability: Record<string, boolean> = {};
      for (const request of data) {
        if (request.requestId) {
          try {
            const result =
              await reimbursementV2ApiService.checkAttachmentExists(
                request.requestId,
              );
            availability[request.requestId] = result.exists;
          } catch (err) {
            console.error(
              `Failed to check attachment for ${request.requestId}:`,
              err,
            );
            availability[request.requestId] = false;
          }
        }
      }
      setAttachmentAvailability(availability);
    } catch (err) {
      setError(t("error.failedToLoadRequests"));
      console.error(t("error.failedToLoadRequests"), err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAttachment = async (requestId: string) => {
    try {
      // Set up PDF viewer with the PDF URL
      const pdfUrl = `/ofc/reimbursement/v2/download/pdf/${requestId}`;
      setSelectedPdfUrl(pdfUrl);
      setSelectedPdfFileName(`${requestId}_attachment.pdf`);
      setPdfViewerOpen(true);
    } catch (err) {
      setError(t("error.failedToViewAttachment"));
      console.error(t("error.failedToViewAttachment"), err);
    }
  };

  const loadCategories = async () => {
    try {
      const data =
        await reimbursementCategoryApiService.getAllActiveCategories();
      setCategories(data);
    } catch (err) {
      console.error(t("error.failedToLoadData"), err);
    }
  };

  const loadTaxClassifications = async () => {
    try {
      const data = await taxClassificationApiService.getAllActive();
      setTaxClassifications(data);
    } catch (err) {
      console.error(t("error.failedToLoadData"), err);
    }
  };

  // Helper function to convert date array [year, month, day] to YYYY-MM-DD string
  const convertDateToString = (date: string | number[]): string => {
    if (typeof date === "string") {
      return date;
    }
    if (Array.isArray(date) && date.length === 3) {
      const [year, month, day] = date;
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    return getLocalDate();
  };

  const handleOpenDialog = (request?: ReimbursementV2Request) => {
    // Check if profile is complete before allowing dialog to open
    if (!isProfileComplete) {
      setError(t("profile.incompleteWarning"));
      return;
    }

    if (request) {
      setEditingRequest(request);
      // Convert date arrays to strings for form fields
      const normalizedRequest = {
        ...request,
        submissionDate: convertDateToString(request.submissionDate as any),
        items:
          request.items?.map((item) => ({
            ...item,
            itemDate: convertDateToString(item.itemDate as any),
          })) || [],
      };
      setFormData(normalizedRequest);
    } else {
      setEditingRequest(null);
      // Initialize with one empty item
      const defaultItem: ReimbursementV2Item = {
        itemDate: getLocalDate(),
        description: "",
        purpose: "",
        quantity: 1,
        pricePerUnit: 0,
        currency: "JPY",
        forexRate: 1,
        taxUnpaid: false, // Always false, not editable in UI
      };
      setFormData({
        categoryId: 0,
        taxClassificationId: 1,
        submissionDate: getLocalDate(), // Will be ignored by backend
        itemDetails: "",
        vendor: "",
        totalAmount: 0,
        items: [defaultItem],
      });
    }
    setAttachmentFiles([]);
    setOpenDialog(true);
  };

  const handleDuplicate = (request: ReimbursementV2Request) => {
    // Check if profile is complete before allowing dialog to open
    if (!isProfileComplete) {
      setError(t("profile.incompleteWarning"));
      return;
    }

    // Create a duplicate with same data but clear item dates
    const duplicatedItems = (request.items || []).map((item) => ({
      ...item,
      itemDate: "", // Clear the date - user must input
    }));

    // Set form data with duplicated content
    setFormData({
      categoryId: request.categoryId,
      taxClassificationId: request.taxClassificationId,
      submissionDate: getLocalDate(), // New submission date
      itemDetails: request.itemDetails,
      vendor: request.vendor,
      totalAmount: request.totalAmount,
      items: duplicatedItems,
    });

    // Not editing an existing request, this is a new one
    setEditingRequest(null);
    setAttachmentFiles([]);
    setOpenDialog(true);
  };

  const handleAddItem = () => {
    const newItem: ReimbursementV2Item = {
      itemDate: getLocalDate(),
      description: "",
      purpose: "",
      quantity: 1,
      pricePerUnit: 0,
      currency: "JPY",
      forexRate: 1,
      taxUnpaid: false, // Always false, not editable in UI
    };
    setFormData({
      ...formData,
      items: [...(formData.items || []), newItem],
    });
  };

  const handleRemoveItem = (index: number) => {
    setFormData({
      ...formData,
      items: (formData.items || []).filter((_, i) => i !== index),
    });
  };

  const handleUpdateItem = (
    index: number,
    updatedItem: ReimbursementV2Item,
  ) => {
    const updatedItems = [...(formData.items || [])];
    updatedItems[index] = updatedItem;

    // Recalculate total amount (rounded to no decimals)
    const newTotal = Math.round(
      updatedItems.reduce((sum, item) => {
        return sum + item.quantity * item.pricePerUnit * item.forexRate;
      }, 0),
    );

    setFormData({
      ...formData,
      items: updatedItems,
      totalAmount: newTotal,
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRequest(null);
    setAttachmentFiles([]);
  };

  const handleSubmit = async () => {
    // Attachments are now optional for both new and edit requests
    // The UI will generate a PDF from the form data
    if (!user?.username) {
      setError(t("error.requiredFieldsMissing"));
      return;
    }

    setLoading(true);
    try {
      // Round total amount before submission
      const roundedFormData = {
        ...formData,
        totalAmount: Math.round(formData.totalAmount || 0),
      };

      // Prepare user info for first page
      const userInfo = {
        firstName: employeeData?.firstName || user?.username || "",
        middleName: employeeData?.middleName,
        lastName: employeeData?.lastName || "",
        homeAddress: employeeData?.homeAddress,
      };

      if (editingRequest) {
        // Edit existing request
        let mergedPdf: File | null = null;
        let fileName: string | undefined = undefined;

        if (attachmentFiles.length > 0) {
          // User provided new attachments - merge them with the reimbursement form
          mergedPdf = await mergeFilesToPdf(
            attachmentFiles,
            roundedFormData,
            userInfo,
          );
          fileName = mergedPdfFileName;
        } else {
          // No new attachments provided - check if there are existing attachments
          try {
            // Check if attachment exists
            const attachmentCheck =
              await reimbursementV2ApiService.checkAttachmentExists(
                editingRequest.requestId!,
              );

            if (attachmentCheck.exists) {
              // Download the existing attachment
              const existingPdfBlob =
                await reimbursementV2ApiService.downloadAttachment(
                  editingRequest.requestId!,
                );

              // Replace the first page with the updated reimbursement form
              mergedPdf = await replaceFirstPageOfPdf(
                existingPdfBlob,
                roundedFormData,
                userInfo,
              );
              fileName = mergedPdfFileName;
            } else {
              // No existing attachment - just create a new PDF with the reimbursement form
              mergedPdf = await mergeFilesToPdf([], roundedFormData, userInfo);
              fileName = mergedPdfFileName;
            }
          } catch (err) {
            console.error("Failed to handle existing attachment:", err);
            // Fallback: create a new PDF with just the reimbursement form
            mergedPdf = await mergeFilesToPdf([], roundedFormData, userInfo);
            fileName = mergedPdfFileName;
          }
        }

        await reimbursementV2ApiService.editReimbursementRequest(
          editingRequest.requestId!,
          roundedFormData,
          mergedPdf,
          user.username,
          fileName,
        );
        setSuccess(t("success.requestUpdated"));
      } else {
        // Submit new request - generate PDF from form (with optional user attachments)
        const mergedPdf = await mergeFilesToPdf(
          attachmentFiles,
          roundedFormData,
          userInfo,
        );

        await reimbursementV2ApiService.submitReimbursementRequest(
          user?.id || 0,
          roundedFormData,
          mergedPdf,
          user?.username || "",
          mergedPdfFileName,
        );
        setSuccess(t("success.requestSubmitted"));
      }

      handleCloseDialog();
      await loadRequests();
    } catch (err) {
      setError(t("error.failedToSubmitRequest"));
      console.error(t("error.failedToSubmitRequest"), err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (requestId: string) => {
    setRequestToDelete(requestId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!requestToDelete) return;

    setLoading(true);
    try {
      await reimbursementV2ApiService.deleteReimbursementRequest(
        requestToDelete,
        user?.username || "",
      );
      setSuccess(t("success.requestDeleted"));
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
      await loadRequests();
    } catch (err) {
      setError(t("error.failedToDeleteRequest"));
      console.error(t("error.failedToDeleteRequest"), err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRequestToDelete(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUBMITTED":
        return "info";
      case "PROCESSING":
        return "warning";
      case "PROCESSED":
        return "success";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">{t("reimbursement.v2.title")}</Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={!isProfileComplete}
            title={!isProfileComplete ? t("profile.incompleteWarning") : ""}
          >
            {t("reimbursement.v2.newRequest")}
          </Button>
        )}
      </Box>

      {!isProfileComplete && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("profile.incompleteWarning")}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading && <CircularProgress />}

      {/* Mobile Card View */}
      {isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {requests.map((request) => (
            <Card key={request.id} variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("reimbursement.v2.requestId")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {request.requestId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={request.status}
                      color={getStatusColor(request.status!)}
                      size="small"
                    />
                    {request.status === "REJECTED" &&
                      request.rejectionComment && (
                        <Tooltip title={request.rejectionComment}>
                          <InfoIcon
                            sx={{
                              fontSize: 18,
                              color: "error.main",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      )}
                  </Box>
                </Box>

                {/* Item and Vendor in same row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1.5,
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("reimbursement.v2.item")}
                    </Typography>
                    <Typography variant="body2">
                      {request.itemDetails}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("reimbursement.v2.vendor")}
                    </Typography>
                    <Typography variant="body2">{request.vendor}</Typography>
                  </Box>
                </Box>

                {/* Amount and Date in same row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1.5,
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("reimbursement.v2.date")}
                    </Typography>
                    <Typography variant="body2">
                      {request.createdAt
                        ? new Date(
                            parseInt(request.createdAt) * 1000,
                          ).toLocaleDateString()
                        : "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("reimbursement.v2.amount")}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="primary"
                    >
                      ¥{request.totalAmount.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>

                {/* Action Buttons - View PDF and Delete only in mobile */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "flex-end",
                    borderTop: 1,
                    borderColor: "divider",
                    pt: 1.5,
                  }}
                >
                  {attachmentAvailability[request.requestId!] ? (
                    <Tooltip title={t("common.view")}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewAttachment(request.requestId!)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={t("reimbursement.v2.attachmentNotAvailable")}
                    >
                      <span>
                        <IconButton size="small" disabled>
                          <VisibilityOffIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Delete Button - Only for SUBMITTED or REJECTED status */}
                  {(request.status === "SUBMITTED" ||
                    request.status === "REJECTED") && (
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(request.requestId!)}
                        color="primary"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("reimbursement.v2.requestId")}</TableCell>
                <TableCell>{t("reimbursement.v2.item")}</TableCell>
                <TableCell>{t("reimbursement.v2.vendor")}</TableCell>
                <TableCell align="right">
                  {t("reimbursement.v2.amount")}
                </TableCell>
                <TableCell>{t("reimbursement.v2.date")}</TableCell>
                <TableCell align="center">
                  {t("reimbursement.v2.status")}
                </TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.requestId}</TableCell>
                  <TableCell>{request.itemDetails}</TableCell>
                  <TableCell>{request.vendor}</TableCell>
                  <TableCell align="right">
                    ¥{request.totalAmount.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {request.createdAt
                      ? new Date(
                          parseInt(request.createdAt) * 1000,
                        ).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <Chip
                        label={request.status}
                        color={getStatusColor(request.status!)}
                        size="small"
                      />
                      {request.status === "REJECTED" &&
                        request.rejectionComment && (
                          <Tooltip title={request.rejectionComment}>
                            <InfoIcon
                              sx={{
                                fontSize: 18,
                                color: "error.main",
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {/* View Attachment Button - Available for all requests with attachments */}
                    {attachmentAvailability[request.requestId!] ? (
                      <Tooltip title={t("common.view")}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleViewAttachment(request.requestId!)
                          }
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title={t("reimbursement.v2.attachmentNotAvailable")}
                      >
                        <span>
                          <IconButton size="small" disabled>
                            <VisibilityOffIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {/* Edit, Duplicate and Delete Buttons - Only for SUBMITTED or REJECTED status */}
                    {(request.status === "SUBMITTED" ||
                      request.status === "REJECTED") && (
                      <>
                        <Tooltip title={t("common.edit")}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(request)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.duplicate")}>
                          <IconButton
                            size="small"
                            onClick={() => handleDuplicate(request)}
                            color="primary"
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.delete")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteClick(request.requestId!)
                            }
                            color="primary"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Submit/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={(_, reason) => {
          // Only close on explicit cancel/submit, not on backdrop click or escape
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCloseDialog();
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={loading}
        PaperProps={{
          sx: {
            // Prevent the entire dialog from re-rendering
            willChange: "auto",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.3rem",
            py: 2,
          }}
        >
          {editingRequest
            ? t("reimbursement.v2.editRequest")
            : t("reimbursement.v2.newRequest")}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, maxHeight: "85vh", overflow: "auto" }}>
          {/* Notification Message */}
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("reimbursement.v2.groupingNotice")}
          </Alert>

          {/* Item and Vendor */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              mb: 2,
              pt: 3,
            }}
          >
            <TextField
              fullWidth
              label={t("reimbursement.v2.item")}
              value={formData.itemDetails}
              onChange={handleItemDetailsChange}
              disabled={loading}
              variant="outlined"
              required
              sx={formData.itemDetails ? normalBorderSx : errorBorderSx}
            />
            <TextField
              fullWidth
              label={t("reimbursement.v2.vendor")}
              value={formData.vendor}
              onChange={handleVendorChange}
              disabled={loading}
              variant="outlined"
              required
              sx={formData.vendor ? normalBorderSx : errorBorderSx}
            />
          </Box>

          {/* Category and Tax Classification */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
            <FormControl
              fullWidth
              variant="outlined"
              required
              sx={formData.categoryId ? normalBorderSx : errorBorderSx}
            >
              <InputLabel sx={{ mt: 1 }}>
                {t("reimbursement.v2.category")}
              </InputLabel>
              <Select
                value={formData.categoryId || 0}
                label={t("reimbursement.v2.category")}
                onChange={handleCategoryChange}
                disabled={loading}
              >
                <MenuItem value={0}>
                  {t("reimbursement.v2.selectCategory")}
                </MenuItem>
                {categoriesMenuItems}
              </Select>
            </FormControl>

            <Box>
              <FormControl
                fullWidth
                variant="outlined"
                required
                sx={
                  formData.taxClassificationId ? normalBorderSx : errorBorderSx
                }
              >
                <InputLabel sx={{ mt: 1 }}>
                  {t("reimbursement.v2.taxClassification")}
                </InputLabel>
                <Select
                  value={formData.taxClassificationId || 1}
                  label={t("reimbursement.v2.taxClassification")}
                  onChange={handleTaxClassificationChange}
                  disabled={loading}
                >
                  {taxClassificationsMenuItems}
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {t("reimbursement.v2.taxClassificationHelp")}
              </Typography>
            </Box>
          </Box>

          {/* Items Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
            >
              {t("reimbursement.v2.items")}
            </Typography>

            {/* Currency Rate Information */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {t("reimbursement.v2.forexRateInfo")}
              </Typography>
            </Alert>
          </Box>

          {(formData.items || []).map((item, index) => (
            <Card
              key={index}
              sx={{
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                  transition: "box-shadow 0.3s ease",
                },
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "primary.main" }}
                  >
                    {t("reimbursement.v2.itemNumber", { number: index + 1 })}
                  </Typography>
                  {(formData.items || []).length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveItem(index)}
                      color="error"
                      title={t("common.delete")}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>

                {/* Item Date and Description */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label={t("reimbursement.v2.itemDate")}
                    type="date"
                    value={item.itemDate}
                    onChange={(e) =>
                      handleUpdateItem(index, {
                        ...item,
                        itemDate: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        ...(item.itemDate
                          ? {}
                          : {
                              "& fieldset": {
                                borderColor: "#d32f2f",
                                borderWidth: 2,
                              },
                            }),
                      },
                    }}
                  />
                  <TextField
                    label={t("reimbursement.v2.description")}
                    value={item.description}
                    onChange={(e) =>
                      handleUpdateItem(index, {
                        ...item,
                        description: e.target.value,
                      })
                    }
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        ...(item.description
                          ? {}
                          : {
                              "& fieldset": {
                                borderColor: "#d32f2f",
                                borderWidth: 2,
                              },
                            }),
                      },
                    }}
                  />
                </Box>

                {/* Purpose and Quantity */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label={t("reimbursement.v2.purpose")}
                    value={item.purpose}
                    onChange={(e) =>
                      handleUpdateItem(index, {
                        ...item,
                        purpose: e.target.value,
                      })
                    }
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        ...(item.purpose
                          ? {}
                          : {
                              "& fieldset": {
                                borderColor: "#d32f2f",
                                borderWidth: 2,
                              },
                            }),
                      },
                    }}
                  />
                  <TextField
                    label={t("reimbursement.v2.quantity")}
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      handleUpdateItem(index, {
                        ...item,
                        quantity: parseInt(e.target.value),
                      })
                    }
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        ...(item.quantity
                          ? {}
                          : {
                              "& fieldset": {
                                borderColor: "#d32f2f",
                                borderWidth: 2,
                              },
                            }),
                      },
                    }}
                  />
                </Box>

                {/* Price Per Unit, Currency, and Forex Rate */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label={t("reimbursement.v2.pricePerUnit")}
                    type="number"
                    value={item.pricePerUnit}
                    onChange={(e) =>
                      handleUpdateItem(index, {
                        ...item,
                        pricePerUnit: parseFloat(e.target.value),
                      })
                    }
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        ...(item.pricePerUnit
                          ? {}
                          : {
                              "& fieldset": {
                                borderColor: "#d32f2f",
                                borderWidth: 2,
                              },
                            }),
                      },
                    }}
                  />
                  <Autocomplete
                    options={CURRENCIES}
                    getOptionLabel={(option) => formatCurrencyDisplay(option)}
                    value={
                      CURRENCIES.find((c) => c.code === item.currency) || null
                    }
                    onChange={(_, newValue) => {
                      const newCurrency = newValue?.code || "JPY";
                      // If changing to JPY, set forex rate to 1
                      const newForexRate =
                        newCurrency === "JPY" ? 1 : item.forexRate;
                      handleUpdateItem(index, {
                        ...item,
                        currency: newCurrency,
                        forexRate: newForexRate,
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("reimbursement.v2.currency")}
                        size="small"
                      />
                    )}
                    size="small"
                    disabled={loading}
                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toUpperCase();
                      return options.filter(
                        (option) =>
                          option.code.includes(inputValue) ||
                          option.name.toUpperCase().includes(inputValue),
                      );
                    }}
                  />
                  <TextField
                    label={t("reimbursement.v2.forexRate")}
                    type="number"
                    value={item.forexRate}
                    onChange={(e) =>
                      handleUpdateItem(index, {
                        ...item,
                        forexRate: parseFloat(e.target.value),
                      })
                    }
                    size="small"
                    disabled={item.currency === "JPY"}
                    InputProps={{
                      readOnly: item.currency === "JPY",
                    }}
                    sx={
                      item.currency === "JPY"
                        ? forexRateJpySx
                        : forexRateNormalSx
                    }
                  />
                </Box>

                {/* Amount in Yen */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  {/* Amount in Yen - Right side */}
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      border: "2px solid",
                      borderColor: "primary.main",
                      minWidth: 100,
                      textAlign: "right",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      {t("reimbursement.v2.amountInYen")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "primary.main" }}
                    >
                      ¥
                      {Math.round(
                        item.quantity * item.pricePerUnit * item.forexRate,
                      ).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            fullWidth
            sx={{ mb: 3, py: 1.5 }}
            disabled={loading}
          >
            {t("reimbursement.v2.addItem")}
          </Button>
          {/* Total Amount (Read-only) */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 1,
                fontWeight: "bold",
                color: "primary.main",
                textAlign: "center",
              }}
            >
              {t("reimbursement.v2.totalAmount")}
            </Typography>
            <TextField
              type="text"
              value={`¥${formData.totalAmount.toLocaleString()}`}
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "primary.light",
                  borderColor: "primary.dark",
                  cursor: "not-allowed",
                  color: "primary.contrastText",
                },
                "& .MuiOutlinedInput-input": {
                  textAlign: "center",
                },
              }}
            />
          </Box>

          {/* Attachments */}
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
            >
              {t("reimbursement.v2.attachments")} *
              {editingRequest && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "warning.main",
                    mt: 0.5,
                    fontWeight: "normal",
                  }}
                >
                  ⚠️ {t("reimbursement.v2.attachmentsRequiredOnEdit")}
                </Typography>
              )}
            </Typography>
            {/* Attachment Requirement Notice */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {t("reimbursement.v2.attachmentRequirementNotice")}
              </Typography>
            </Alert>
            <FileUpload
              onFileSelect={(files) => setAttachmentFiles(files || [])}
              label={t("reimbursement.v2.attachments")}
              disabled={loading}
              selectedFiles={attachmentFiles}
              multiple={true}
              maxFiles={5}
              maxFileSize={5 * 1024 * 1024} // 5MB
              acceptedFileTypes={[".pdf", ".jpg", ".jpeg", ".png"]}
              required={false}
            />
            <Typography
              variant="caption"
              color="info.main"
              sx={{ display: "block", mt: 1 }}
            >
              ℹ️ {t("reimbursement.v2.attachmentOptionalInfo")}
            </Typography>
            {attachmentFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                  {t("reimbursements.fileMergeNotice")}
                </Typography>
                <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                  {t("reimbursements.fileRenameNotice")}
                </Typography>
                {mergedPdfFileName && (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      mt: 0.5,
                      fontWeight: "bold",
                      p: 1,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    {t("reimbursements.mergedFileName")}: {mergedPdfFileName}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button onClick={handleCloseDialog} disabled={loading} sx={{ px: 3 }}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              loading || !formData.categoryId || !formData.taxClassificationId
            }
            sx={{ px: 4, py: 1 }}
          >
            {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {loading
              ? t("common.submitting") || "Submitting..."
              : t("common.submit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("common.delete")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t("confirm.deleteRequest")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <SubmitFeedback
        loading={loading}
        error={error}
        success={success}
        onCloseError={() => setError(undefined)}
        onCloseSuccess={() => setSuccess(undefined)}
      />

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={selectedPdfUrl}
        fileName={selectedPdfFileName}
      />
    </Container>
  );
};

export default ReimbursementV2Page;
